import React, { useState, useEffect } from "react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { X } from "lucide-react";
import {
  TaskFilter,
  NewTaskFilter,
  List,
  Tag,
  DateFilterValue,
  WorkHoursFilterValue,
  TaskScope,
} from "../../types";
import { useOrderedLists } from "../../hooks/queries/useOrderedLists";
import SharedListMark from "../ui/SharedListMark";

interface FilterDialogProps {
  filter: TaskFilter | null;
  lists: List[];
  tags: Tag[];
  onClose: () => void;
  onSave: (filter: NewTaskFilter) => void;
  isEditing: boolean;
}

const FilterDialog: React.FC<FilterDialogProps> = ({
  filter,
  lists,
  tags,
  onClose,
  onSave,
  isEditing,
}) => {
  useEscapeKey(onClose);

  const [filterName, setFilterName] = useState("");
  const [selectedDateFilters, setSelectedDateFilters] = useState<
    DateFilterValue[]
  >([]);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedWorkHours, setSelectedWorkHours] =
    useState<WorkHoursFilterValue | null>(null);
  const [taskScope, setTaskScope] = useState<TaskScope>("all");
  const { lists: visibleLists, sharedListIds } = useOrderedLists(lists);

  // Initialize state from filter when editing
  useEffect(() => {
    if (filter) {
      setFilterName(filter.name);
      setSelectedDateFilters(filter.date_filters);
      setSelectedLists(filter.list_ids);
      setSelectedPriorities(filter.priorities);
      setSelectedTags(filter.tag_ids);
      setSelectedWorkHours(filter.work_hours_filter ?? null);
      setTaskScope(filter.task_scope ?? "all");
    }
  }, [filter]);

  const toggleDateFilter = (value: DateFilterValue) => {
    setSelectedDateFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleList = (listId: string) => {
    setSelectedLists((prev) =>
      prev.includes(listId)
        ? prev.filter((id) => id !== listId)
        : [...prev, listId],
    );
  };

  const togglePriority = (priority: number) => {
    setSelectedPriorities((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority],
    );
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const handleSave = () => {
    if (!filterName.trim()) return;

    const newFilter: NewTaskFilter = {
      name: filterName.trim(),
      date_filters: selectedDateFilters,
      list_ids: selectedLists,
      priorities: selectedPriorities,
      tag_ids: selectedTags,
      work_hours_filter: selectedWorkHours,
      task_scope: taskScope,
    };

    onSave(newFilter);
  };

  const workHoursOptions: {
    value: WorkHoursFilterValue | null;
    label: string;
    emoji: string;
  }[] = [
    { value: null, label: "Any", emoji: "📋" },
    { value: "only", label: "Work hours only", emoji: "💼" },
    { value: "exclude", label: "Hide work hours", emoji: "🌙" },
  ];

  const dateFilterOptions: { value: DateFilterValue; label: string }[] = [
    { value: "today", label: "Today" },
    { value: "planned", label: "Scheduled" },
    { value: "no-date", label: "Unscheduled" },
    { value: "overdue", label: "Overdue" },
    { value: "deadline", label: "With deadline" },
  ];

  // Shared lists (#286): which Tasks this filter's other criteria apply to.
  const taskScopeOptions: { value: TaskScope; label: string; emoji: string }[] = [
    { value: "all", label: "All tasks", emoji: "📋" },
    { value: "mine", label: "Only my tasks", emoji: "🙋" },
    { value: "shared", label: "Only tasks shared with me", emoji: "🤝" },
  ];

  const priorityOptions = [
    { value: 1, label: "High", color: "border-l-red-700" },
    { value: 2, label: "Medium", color: "border-l-orange-500" },
    { value: 3, label: "Low", color: "border-l-yellow-400" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 p-2 sm:p-4 animate-dialog-backdrop">
      <div role="dialog" aria-label="Create Filter" className="bg-warm-bg-card rounded-xl border border-warm-border p-4 sm:p-6 w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto animate-dialog-panel">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-warm-text-primary">
            {isEditing ? "Edit Filter" : "Create Filter"}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close filter dialog"
            className="text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1 -m-1"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-5">
          {/* Filter Name */}
          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Filter Name
            </label>
            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Enter filter name..."
              className="w-full bg-warm-bg-elevated border border-warm-border rounded-soft px-3 py-2 text-sm text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
              autoFocus
            />
          </div>

          {/* Priorities */}
          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Priority
            </label>
            <div className="flex flex-wrap gap-2">
              {priorityOptions.map((option) => {
                const isSelected = selectedPriorities.includes(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => togglePriority(option.value)}
                    className={`flex-1 min-w-[90px] px-3 py-2 rounded-card border-l-4 text-sm transition-all duration-150 ease-in-out text-left ${
                      isSelected
                        ? `${option.color} bg-warm-bg-card border border-warm-border shadow-soft-md text-warm-text-primary font-medium`
                        : `${option.color} bg-warm-bg-elevated border border-warm-border text-warm-text-secondary opacity-60 hover:opacity-90 hover:shadow-soft-sm`
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lists — owned and accepted-Membership only. An unanswered
              Invitation is not a List you can filter by yet. */}
          {visibleLists.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                Lists
              </label>
              <div className="flex flex-wrap gap-2">
                {visibleLists.map((list) => {
                  const isSelected = selectedLists.includes(list.id);
                  const isShared = !!sharedListIds?.has(list.id);
                  return (
                    <button
                      key={list.id}
                      onClick={() => toggleList(list.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ease-in-out flex items-center gap-1.5 ${
                        isSelected
                          ? "bg-warm-blue text-white"
                          : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-text-tertiary"
                      }`}
                    >
                      <span>{list.emoji}</span>
                      <span>{list.name}</span>
                      {isShared && (
                        <SharedListMark
                          size={11}
                          className={isSelected ? "text-white" : "text-warm-blue"}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ease-in-out flex items-center gap-1.5 ${
                        isSelected
                          ? "text-white"
                          : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-text-tertiary"
                      }`}
                      style={isSelected ? { backgroundColor: tag.color } : undefined}
                    >
                      {!isSelected && (
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Date Filter */}
          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Date Filter
            </label>
            <div className="flex flex-wrap gap-2">
              {dateFilterOptions.map((option) => {
                const isSelected = selectedDateFilters.includes(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleDateFilter(option.value)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ease-in-out ${
                      isSelected
                        ? "bg-warm-blue text-white"
                        : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-text-tertiary"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Work hours */}
          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Work hours
            </label>
            <div className="flex flex-wrap gap-2">
              {workHoursOptions.map((option) => {
                const isSelected = selectedWorkHours === option.value;
                return (
                  <button
                    key={option.label}
                    onClick={() => setSelectedWorkHours(option.value)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ease-in-out flex items-center gap-1.5 ${
                      isSelected
                        ? "bg-warm-blue text-white"
                        : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-text-tertiary"
                    }`}
                  >
                    <span>{option.emoji}</span>
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shared lists (#286) — which Tasks this filter applies to */}
          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Tasks
            </label>
            <div className="flex flex-wrap gap-2">
              {taskScopeOptions.map((option) => {
                const isSelected = taskScope === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setTaskScope(option.value)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ease-in-out flex items-center gap-1.5 ${
                      isSelected
                        ? "bg-warm-blue text-white"
                        : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-text-tertiary"
                    }`}
                  >
                    <span>{option.emoji}</span>
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-6 pt-4 border-t border-warm-border">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-warm-text-secondary hover:text-warm-text-primary transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!filterName.trim()}
            className="w-full sm:w-auto px-4 py-2 bg-warm-blue text-white rounded-soft text-sm hover:bg-warm-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEditing ? "Update Filter" : "Create Filter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterDialog;
